import React, { useState, useEffect, memo, useMemo, useCallback } from "react";
import { withI18n } from "@lingui/react";
import { Helmet } from "react-helmet";
import { t } from "@lingui/macro";
import svgLogo from "../Header/assets/WeGoTripLogo.svg";
import { useWebPSupport } from "../../hooks/useWebPSupport";
import { BackgroundImage } from "./BackgroundImage";
import { Header } from "./Header";
import "./TopBar.css";

const backgroundDefaultImageWebp =
  "https://wgt-prod-storage.s3.eu-west-3.amazonaws.com/media/store/WGT+%D0%BE%D0%B1%D0%BB%D0%BE%D0%B6%D0%BA%D0%B0+%D0%BA%D0%BE%D0%BF%D0%B8%D1%8F+2.webp";
const backgroundDefaultImageJpg =
  "https://wgt-prod-storage.s3.eu-west-3.amazonaws.com/media/store/WGT+%D0%BE%D0%B1%D0%BB%D0%BE%D0%B6%D0%BA%D0%B0+%D0%BA%D0%BE%D0%BF%D0%B8%D1%8F+2.jpg";

/**
 * Block with search and advertising abilities
 * @param {Object} props
 * @param {String?} props.background - image got via `import` directive, will be default if not given
 */
const TopBar = memo(function TopBar({ background, i18n }) {
  const [showImage, setShowImage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isWebPSupported = useWebPSupport();

  const imageSrc = useMemo(
    () => background || (isWebPSupported ? backgroundDefaultImageWebp : backgroundDefaultImageJpg),
    [background, isWebPSupported],
  );

  const handleImageLoad = useCallback(() => {
    setShowImage(true);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const preloadImage = () => {
      const img = new Image();
      img.src = imageSrc;
      img.onload = handleImageLoad;
      img.onerror = () => {
        setIsLoading(false);
        console.error("Failed to load background image");
      };
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(preloadImage);
    } else {
      setTimeout(preloadImage, 0);
    }

    return () => {
      setShowImage(false);
      setIsLoading(true);
    };
  }, [imageSrc, handleImageLoad]);

  const metaTags = useMemo(
    () => ({
      title: i18n._(t`Self-guided audio tours on the mobile app`),
      description: i18n._(
        t`Book self-guided audio tours with skip-the-line tickets to museums, galleries, and many other attractions.`,
      ),
      image: svgLogo,
      siteName: "WeGoTrip",
      type: "website",
    }),
    [i18n],
  );

  return (
    <header className="TopBar" role="banner" aria-label="Main header">
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={imageSrc}
          type={isWebPSupported ? "image/webp" : "image/jpeg"}
        />
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:image" content={metaTags.image} />
        <meta property="og:site_name" content={metaTags.siteName} />
        <meta property="og:type" content={metaTags.type} />
      </Helmet>
      <div
        className={`TopBar__functional ${isLoading ? "TopBar__functional--loading" : ""}`}
        role="region"
        aria-label="Header content"
      >
        {showImage && (
          <BackgroundImage alt="Background image" src={imageSrc} onLoad={handleImageLoad} />
        )}
        <Header />
      </div>
    </header>
  );
});

export default withI18n()(TopBar);
